function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  // Preserve role="menuitem" if already set, otherwise set role="button"
  if (!summary.hasAttribute('role') || summary.getAttribute('role') !== 'menuitem') {
    summary.setAttribute('role', 'button');
  }

  // ADA #178 - The mobile drawer submenus ("Shop", "About") drill down into a
  // panel with its own Back button; they do not expand in place, so they carry
  // no aria-expanded / aria-controls. MenuDrawer handles their clicks.
  if (summary.closest('header-drawer')) return;

  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

/*
  Elements that match the focusable selector but are not rendered (display: none,
  closed <details>, hidden gallery slides) cannot receive focus, so they must not
  act as the first/last stop of the trap or the loop breaks and focus escapes.
*/
function isFocusableVisible(element) {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({ visibilityProperty: true });
  }
  return element.getClientRects().length > 0;
}

function trapFocus(container, elementToFocus = container, focusOptions) {
  removeTrapFocus();

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key

    /*
      Resolve the focusable list on every Tab instead of once when the trap is
      set. Modal content changes after opening (quick add re-runs product scripts,
      Skio injects its picker, variant changes re-render the info block), so a
      list captured at open time points at detached nodes and lets focus leave.
    */
    const elements = getFocusableElements(container).filter(isFocusableVisible);
    if (!elements.length) {
      event.preventDefault();
      return;
    }

    const first = elements[0];
    const last = elements[elements.length - 1];
    const target = event.target;

    // Focus already left the container: bring it back in on the next Tab.
    if (!container.contains(target)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    // On the last focusable element and tab forward, focus the first element.
    if (target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
      return;
    }

    // On the first focusable element (or the container itself) and tab backward, focus the last element.
    if ((target === container || target === first || !elements.includes(target)) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('keydown', trapFocusHandlers.keydown);

  elementToFocus.focus(focusOptions);

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function setupRebuyFocusManagement() {
  console.log('[Rebuy Focus] Initializing Rebuy focus management');
  let rebuyFocusTimeout = null;
  let isRebuyOpen = false;
  let previousActiveElement = null;
  let clickedAddToCartButton = null;

  // Find Rebuy flyout container
  const findRebuyFlyout = () => {
    const selectors = [
      '.rebuy-cart__flyout',
      '.rebuy-cart-flyout',
      '[class*="rebuy"][class*="flyout"]'
    ];
    
    console.log('[Rebuy Focus] Searching for Rebuy flyout with selectors:', selectors);
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log('[Rebuy Focus] Found element with selector:', selector, element);
        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        console.log('[Rebuy Focus] Element styles - display:', style.display, 'visibility:', style.visibility, 'opacity:', style.opacity);
        console.log('[Rebuy Focus] Element dimensions - width:', rect.width, 'height:', rect.height);
        // Check if visible - use dimensions instead of offsetParent since it can be null for fixed/absolute elements
        if (style.display !== 'none' && 
            style.visibility !== 'hidden' && 
            style.opacity !== '0' &&
            rect.width > 0 && 
            rect.height > 0) {
          console.log('[Rebuy Focus] Flyout is visible, returning element');
          return element;
        } else {
          console.log('[Rebuy Focus] Element found but not visible');
        }
      }
    }
    console.log('[Rebuy Focus] No visible Rebuy flyout found');
    return null;
  };

  // Focus Rebuy flyout after cart update
  const focusRebuyFlyout = () => {
    console.log('[Rebuy Focus] Cart update received, scheduling focus attempt');
    
    // Use the clicked button if we tracked it, otherwise fall back to activeElement
    let activeEl = null;
    if (clickedAddToCartButton) {
      // Verify the button is still in the DOM
      if (document.body.contains(clickedAddToCartButton)) {
        console.log('[Rebuy Focus] Using tracked clicked button:', clickedAddToCartButton);
        console.log('[Rebuy Focus] Tracked button id:', clickedAddToCartButton.id);
        activeEl = clickedAddToCartButton;
        clickedAddToCartButton = null; // Clear after using
      } else {
        console.log('[Rebuy Focus] Tracked button no longer in DOM, falling back to activeElement');
        activeEl = document.activeElement;
        clickedAddToCartButton = null;
      }
    } else {
      console.log('[Rebuy Focus] No tracked button found, using activeElement:', document.activeElement);
      activeEl = document.activeElement;
    }
    
    // If body or html is focused and we don't have a clicked button, try to find a better element
    // But don't use querySelector as it will always find the first one
    if (activeEl === document.body || activeEl === document.documentElement) {
      console.log('[Rebuy Focus] Body/html was focused, but no tracked button available');
      console.log('[Rebuy Focus] Cannot reliably find the correct button without click tracking');
      // Keep body/html as fallback rather than guessing
    }
    
    // Don't store the close button if it's already focused (means Rebuy is already open)
    if (activeEl && activeEl.classList && activeEl.classList.contains('rebuy-cart__flyout-close')) {
      console.log('[Rebuy Focus] Close button is focused, keeping previous stored element');
      // Keep the existing previousActiveElement if we have one
    } else {
      previousActiveElement = activeEl;
      console.log('[Rebuy Focus] Stored previous active element:', previousActiveElement);
      console.log('[Rebuy Focus] Previous element tag:', previousActiveElement ? previousActiveElement.tagName : 'N/A');
      console.log('[Rebuy Focus] Previous element id:', previousActiveElement ? previousActiveElement.id : 'N/A');
      console.log('[Rebuy Focus] Previous element class:', previousActiveElement ? previousActiveElement.className : 'N/A');
    }
    
    // Clear any existing timeout
    if (rebuyFocusTimeout) {
      console.log('[Rebuy Focus] Clearing existing timeout');
      clearTimeout(rebuyFocusTimeout);
    }

    // Wait 1 second for Rebuy to render
    rebuyFocusTimeout = setTimeout(() => {
      console.log('[Rebuy Focus] Timeout fired, attempting to find and focus flyout');
      
      const flyout = findRebuyFlyout();
      if (flyout) {
        console.log('[Rebuy Focus] Flyout found, finding focusable element');
        isRebuyOpen = true;
        
        // Try to find the close button first, or first focusable element
        const closeButton = flyout.querySelector('.rebuy-cart__flyout-close');
        const focusableElements = getFocusableElements(flyout);
        const elementToFocus = closeButton || focusableElements[0] || flyout;
        
        console.log('[Rebuy Focus] Close button found:', !!closeButton);
        console.log('[Rebuy Focus] Focusable elements found:', focusableElements.length);
        console.log('[Rebuy Focus] Element to focus:', elementToFocus);
        
        trapFocus(flyout, elementToFocus);
        console.log('[Rebuy Focus] Focus trapped on flyout');
        
        // Also try to focus directly after a brief delay to ensure it works
        setTimeout(() => {
          if (elementToFocus && typeof elementToFocus.focus === 'function') {
            console.log('[Rebuy Focus] Attempting direct focus on element');
            elementToFocus.focus();
            console.log('[Rebuy Focus] Direct focus called, current activeElement:', document.activeElement);
          }
        }, 100);
      } else {
        console.log('[Rebuy Focus] Flyout not found after timeout');
        // Clear previous element if flyout didn't open
        previousActiveElement = null;
      }
    }, 1000);
  };

  // Restore focus to previous element
  const restoreFocusToMain = () => {
    console.log('[Rebuy Focus] Restoring focus to previous element');
    if (rebuyFocusTimeout) {
      clearTimeout(rebuyFocusTimeout);
      rebuyFocusTimeout = null;
    }
    
    isRebuyOpen = false;
    
    // Use a small delay to let Rebuy finish closing
    setTimeout(() => {
      // Restore to the element that had focus before Rebuy opened
      // Check if element exists and is in the document (handle body/html specially)
      const elementExists = previousActiveElement && (
        previousActiveElement === document.body || 
        previousActiveElement === document.documentElement ||
        document.body.contains(previousActiveElement)
      );
      
      if (elementExists) {
        console.log('[Rebuy Focus] Previous active element found:', previousActiveElement);
        console.log('[Rebuy Focus] Element tag:', previousActiveElement.tagName, 'Element type:', previousActiveElement.type);
        
        // Remove trap focus first
        removeTrapFocus();
        
        // Try to focus the element directly
        if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
          // Scroll element into view if needed (skip for body/html)
          if (previousActiveElement !== document.body && previousActiveElement !== document.documentElement) {
            previousActiveElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          // Focus the element
          setTimeout(() => {
            if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
              previousActiveElement.focus();
              console.log('[Rebuy Focus] Focus restored to previous element, current activeElement:', document.activeElement);
            }
          }, 50);
        } else {
          console.log('[Rebuy Focus] Previous element is not focusable, using removeTrapFocus');
          if (previousActiveElement) {
            removeTrapFocus(previousActiveElement);
          } else {
            removeTrapFocus();
          }
        }
        previousActiveElement = null;
      } else if (previousActiveElement) {
        // ADA #188 - The opener is gone, usually because the cart re-rendered the
        // card it was in. Move focus as close to it as possible: the header cart
        // icon is where the trigger normally sits and it reopens the cart.
        console.log('[Rebuy Focus] Previous element not available or not in DOM');
        console.log('[Rebuy Focus] Previous element was:', previousActiveElement);
        console.log('[Rebuy Focus] In DOM check:', document.body.contains(previousActiveElement));
        
        const fallbackElement = document.querySelector('#cart-icon-bubble, .header__icon--cart') || document.getElementById('MainContent');
        if (fallbackElement) {
          console.log('[Rebuy Focus] Falling back to:', fallbackElement);
          removeTrapFocus();
          setTimeout(() => {
            fallbackElement.focus();
            console.log('[Rebuy Focus] Focus restored to fallback, current activeElement:', document.activeElement);
          }, 50);
        } else {
          console.log('[Rebuy Focus] No fallback element found');
          removeTrapFocus();
        }
        previousActiveElement = null;
      } else {
        // ADA #188 - Nothing opened the cart: it opened by itself on page load
        // (?viewcart=true), so focus belongs on the body and the next Tab starts
        // from the top of the page.
        console.log('[Rebuy Focus] No opener recorded, returning focus to the body');
        removeTrapFocus();
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
      }
    }, 100);
  };

  // Track clicks on add to cart buttons to identify which one was clicked
  document.addEventListener('click', (event) => {
    const target = event.target;
    
    // Check if the clicked element is a button or inside a button
    let button = null;
    if (target.tagName === 'BUTTON') {
      button = target;
    } else {
      button = target.closest('button');
    }
    
    if (button) {
      // Check if it's an add to cart button by various criteria
      const isQuickAdd = button.classList.contains('quick-add__submit');
      const isProductForm = button.classList.contains('product-form__submit');
      const hasProductFormId = button.hasAttribute('data-product-form-id');
      const isSubmitButton = button.type === 'submit';
      
      // Check the form if it exists
      const form = button.closest('form');
      const formHasCartAdd = form && form.action && form.action.includes('cart/add');
      
      // Check if button name or aria-label suggests add to cart
      const nameOrAria = (button.name && button.name.includes('add')) || 
                         (button.getAttribute('aria-label') && button.getAttribute('aria-label').toLowerCase().includes('add to cart'));
      
      if (isQuickAdd || isProductForm || hasProductFormId || formHasCartAdd || (isSubmitButton && nameOrAria)) {
        clickedAddToCartButton = button;
        console.log('[Rebuy Focus] Tracked clicked add to cart button:', button);
        console.log('[Rebuy Focus] Button id:', button.id);
        console.log('[Rebuy Focus] Button class:', button.className);
        console.log('[Rebuy Focus] Button name:', button.name);
        console.log('[Rebuy Focus] Form action:', form ? form.action : 'no form');
      } else {
        console.log('[Rebuy Focus] Button clicked but not an add to cart button:', button);
      }
    }
  }, true);

  // Subscribe to cart updates
  console.log('[Rebuy Focus] Subscribing to cart update events');
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    console.log('[Rebuy Focus] Cart update event received');
    focusRebuyFlyout();
  });

  // ADA #188 - Track the cart through Rebuy's own events instead of the close
  // button and the Escape key. Rebuy fires them for every way in and out of the
  // cart (cart icon, add to cart, close button, background overlay, Escape and
  // Continue Shopping), so the opener is recorded and restored whichever one the
  // user takes. Opening through add to cart also goes through cartUpdate above,
  // which is what moves focus into the cart and traps it there.
  document.addEventListener('rebuy:smartcart.show', () => {
    isRebuyOpen = true;

    const opener = clickedAddToCartButton || document.activeElement;
    clickedAddToCartButton = null;

    // Nothing worth returning to when the cart opens by itself on page load, or
    // when Rebuy re-opens a cart that already has focus inside it.
    if (!opener || opener === document.body || opener === document.documentElement) return;
    if (typeof opener.closest === 'function' && opener.closest('#rebuy-cart, .rebuy-cart__flyout')) return;

    previousActiveElement = opener;
    console.log('[Rebuy Focus] Stored opener on smartcart.show:', previousActiveElement);
  });

  document.addEventListener('rebuy:smartcart.hide', () => {
    if (!isRebuyOpen) return;

    console.log('[Rebuy Focus] Cart closed, restoring focus to the opener');
    restoreFocusToMain();
  });
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    // ADA #118 / #179 - In the header drawer the main toggle and the submenus are
    // <button>s inside plain `[data-menu-toggle]` wrappers instead of
    // <details>/<summary>. The facets drawer still uses <details>, so both shapes
    // are supported: a wrapper carries the same `open` attribute <details>
    // manages natively, and this class sets it by hand when there is no <details>.
    this.mainDetailsToggle = this.querySelector('[data-menu-toggle], details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  // Wrappers that carry the `open` state, whichever markup they use.
  static get toggleSelector() {
    return 'details, [data-menu-toggle]';
  }

  static get openToggleSelector() {
    return 'details[open], [data-menu-toggle][open]';
  }

  // The control that toggles a wrapper: the <summary> of a <details>, or the <button> of a plain wrapper.
  static toggleButtonOf(toggle) {
    return toggle.querySelector(':scope > summary, :scope > button');
  }

  get mainToggleButton() {
    return MenuDrawer.toggleButtonOf(this.mainDetailsToggle);
  }

  // ADA #117 - The button inside the main drawer that closes it. When there is one, the
  // main toggle button only opens the drawer. The facets drawer has none: its <summary>
  // opens and closes.
  get mainCloseButton() {
    return this.mainDetailsToggle.querySelector('[data-menu-close]');
  }

  // Where focus is trapped while the main drawer is open: the drawer itself when it has
  // its own close button, the whole wrapper when the toggle button is the way out.
  get mainFocusTrap() {
    const closeButton = this.mainCloseButton;
    return closeButton ? closeButton.closest('.menu-drawer') : this.mainDetailsToggle;
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    const toggleButtons = Array.from(this.querySelectorAll('[data-menu-toggle] > button'));
    toggleButtons.forEach((button) => button.addEventListener('click', this.onSummaryClick.bind(this)));
    const mainCloseButton = this.mainCloseButton;
    if (mainCloseButton) mainCloseButton.addEventListener('click', this.onMainCloseButtonClick.bind(this));
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => {
      if (toggleButtons.includes(button) || button === mainCloseButton) return;
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest(MenuDrawer.openToggleSelector);
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainToggleButton)
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, summaryElement.nextElementSibling.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      // ADA #117 - a drawer with its own close button is not closed by the button that opens it.
      if (isOpen && this.mainCloseButton) return;
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      // A <details> submenu toggles itself; a plain wrapper is toggled here.
      if (detailsElement.tagName !== 'DETAILS') {
        if (isOpen) return this.closeSubmenu(detailsElement);
        detailsElement.setAttribute('open', '');
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        // ADA #178 - the header drawer submenus carry no aria-expanded; the facets ones do.
        if (summaryElement.hasAttribute('aria-expanded')) summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  // <details> opens itself when its <summary> is clicked; a plain wrapper needs the attribute set here.
  setMainToggleOpen() {
    if (this.mainDetailsToggle.tagName !== 'DETAILS') this.mainDetailsToggle.setAttribute('open', '');
  }

  openMenuDrawer(summaryElement) {
    this.setMainToggleOpen();
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    // ADA #118 - the header drawer button is a dialog trigger and carries no aria-expanded; the facets <summary> does.
    if (summaryElement.hasAttribute('aria-expanded')) summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    const mainSummary = this.mainToggleButton;
    if (mainSummary && mainSummary.hasAttribute('aria-expanded')) {
      mainSummary.setAttribute('aria-expanded', false);
    }
    this.mainDetailsToggle.querySelectorAll(MenuDrawer.toggleSelector).forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      const summary = MenuDrawer.toggleButtonOf(details);
      if (summary && summary.hasAttribute('aria-expanded')) {
        summary.setAttribute('aria-expanded', false);
      }
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent && elementToFocus && elementToFocus.hasAttribute('aria-expanded')) {
      elementToFocus.setAttribute('aria-expanded', false);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest(MenuDrawer.toggleSelector);
    this.closeSubmenu(detailsElement);
  }

  onMainCloseButtonClick(event) {
    this.closeMenuDrawer(event, this.mainToggleButton);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    const summary = MenuDrawer.toggleButtonOf(detailsElement);
    if (summary.hasAttribute('aria-expanded')) summary.setAttribute('aria-expanded', false);
    removeTrapFocus(summary);
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        const openParent = detailsElement.parentElement && detailsElement.parentElement.closest(MenuDrawer.openToggleSelector);
        if (openParent) {
          trapFocus(
            openParent === this.mainDetailsToggle ? this.mainFocusTrap : openParent,
            MenuDrawer.toggleButtonOf(detailsElement)
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.setDialogSemantics();
  }

  // ADA #118 - The menu button carries no aria-expanded or aria-controls (the audit asks
  // for a dialog trigger, not a disclosure), so the drawer itself has to tell screen
  // reader users what opened: it is a modal dialog, named after the button that opens
  // it. This is set here rather than in the Liquid because without JavaScript the same
  // markup is plain navigation shown in place, and a modal dialog there would hide the
  // rest of the page from screen readers. It depends on the close button inside the
  // drawer (ADA #117): a modal dialog must be closable from within.
  setDialogSemantics() {
    const label = this.mainToggleButton && this.mainToggleButton.getAttribute('aria-label');
    if (!this.mainCloseButton || !label) return;

    const drawer = this.mainFocusTrap;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', label);
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    this.setMainToggleOpen();
    // Reading the menu button's position here also computes the drawer's closed style,
    // which is the state its opening transition starts from.
    this.followMenuButton();
    const closeButton = this.mainCloseButton;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      // ADA #117 - The drawer is hidden until this class is set, so focus can only move
      // to its close button now. It is still sliding in: do not let focus() scroll to it.
      if (closeButton) trapFocus(this.mainFocusTrap, closeButton, { preventScroll: true });
    });

    if (summaryElement.hasAttribute('aria-expanded')) summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    if (!closeButton) trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.followMenuButtonFrame);
  }

  // ADA #117 - The close button lives inside the drawer but has to sit exactly over the
  // menu button in the header, which is hidden while the drawer is open. The drawer is
  // fixed to the top left corner of the viewport, so the menu button's position in the
  // viewport is the close button's position inside the drawer.
  positionCloseButton() {
    const closeButton = this.mainCloseButton;
    if (!closeButton) return;

    const { top, left } = this.mainToggleButton.getBoundingClientRect();
    const position = { '--menu-button-top': `${top}px`, '--menu-button-left': `${left}px` };
    Object.keys(position).forEach((property) => {
      if (closeButton.style.getPropertyValue(property) !== position[property]) {
        closeButton.style.setProperty(property, position[property]);
      }
    });
  }

  // The header can still move after the drawer opens: it may be sliding into view when
  // the menu button is pressed, the page can keep scrolling for a moment, the viewport
  // can be resized. One measurement would leave the close button behind, so it follows
  // the menu button on every frame until the drawer closes.
  followMenuButton() {
    cancelAnimationFrame(this.followMenuButtonFrame);
    if (!this.mainCloseButton) return;

    const follow = () => {
      if (!this.isConnected || !this.mainDetailsToggle.hasAttribute('open')) return;
      this.positionCloseButton();
      this.followMenuButtonFrame = requestAnimationFrame(follow);
    };
    follow();
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

// Initialize Skio card price mode toggling for product cards
(() => {
  function updateCardMode(selectEl) {
    if (!selectEl) return;
    const card = selectEl.closest('.product-card-wrapper');
    if (!card) return;
    const isSubscription = !!selectEl.value;
    card.classList.toggle('skio-mode--subscription', isSubscription);
  }

  function initSkioCardSelects(root = document) {
    const selects = root.querySelectorAll('.card-skio-frequency');
    selects.forEach((sel) => {
      updateCardMode(sel);
      if (!sel.__skioBound) {
        sel.addEventListener('change', () => updateCardMode(sel));
        sel.__skioBound = true;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSkioCardSelects());
  } else {
    initSkioCardSelects();
  }

  // Re-init on section renders (Shopify theme editor and dynamic loads)
  document.addEventListener('shopify:section:load', (e) => initSkioCardSelects(e.target));
  document.addEventListener('shopify:section:select', (e) => initSkioCardSelects(e.target));
})();

// Ensure Skio radio inputs stay in the keyboard tab order
(() => {
  function updateSelectTabIndex(root = document) {
    root.querySelectorAll('.skio-frequency').forEach((select) => {
      const container = select.closest('.skio-group-container');
      const radio = container?.querySelector('.skio-group-input');
      if (radio && !radio.checked) {
        select.setAttribute('tabindex', '-1');
      } else {
        select.removeAttribute('tabindex');
      }
    });
  }

  function bindSkioRadioTabbing(root = document) {
    const allInputs = Array.from(root.querySelectorAll('.skio-group-input'));
    const inputs = allInputs.filter(input => !input.__skioTabBound);
    if (!inputs.length) return;

    inputs.forEach((input) => {
      input.__skioTabBound = true;
      let label = input.nextElementSibling?.matches('.skio-group-label') 
        ? input.nextElementSibling 
        : null;
      
      if (!label && input.labels && input.labels.length > 0) {
        label = input.labels[0];
      } else if (!label && input.id) {
        const escapedId = CSS.escape(input.id);
        label = document.querySelector(`label[for="${escapedId}"]`);
      }

      input.addEventListener('keydown', (e) => {
        if (e.key === ' ') {
          e.preventDefault();
          if (label) label.click();
          input.dispatchEvent(new Event('change', { bubbles: true }));
          updateSelectTabIndex();
          return;
        }

        if (e.key !== 'Tab') return;
        const currentIndex = allInputs.indexOf(input);
        if (currentIndex === -1) return;

        if (e.shiftKey) {
          const prev = allInputs[currentIndex - 1];
          if (prev) {
            e.preventDefault();
            prev.focus();
          }
        } else {
          const next = allInputs[currentIndex + 1];
          if (next) {
            e.preventDefault();
            next.focus();
          }
        }
      });

      input.addEventListener('change', () => updateSelectTabIndex());
    });

    updateSelectTabIndex(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindSkioRadioTabbing());
  } else {
    bindSkioRadioTabbing();
  }

  document.addEventListener('shopify:section:load', (e) => bindSkioRadioTabbing(e.target));
  document.addEventListener('shopify:section:select', (e) => bindSkioRadioTabbing(e.target));
})();

// Handle card product Skio frequency selection before form submission
(function() {
  function handleCardProductSubmit(event) {
    const button = event.target.closest('button[data-product-form-id]');
    if (!button || button.type !== 'submit') return;
    
    const productFormId = button.getAttribute('data-product-form-id');
    const card = button.closest('.product-card-wrapper');
    if (!card) return;
    
    const frequencySelect = card.querySelector('.card-skio-frequency');
    const hiddenInput = document.getElementById(`sp-${productFormId}`);
    
    if (hiddenInput && frequencySelect) {
      hiddenInput.value = frequencySelect.value || '';
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"][data-product-form-id]');
    if (submitButton) {
      handleCardProductSubmit({ target: submitButton });
    }
  }, true);
})();

// Give the Skio subscription radio set an accessible name.
// The picker markup comes from a Shopify app block, so the legend that names the
// radio group is added here instead of in the app template.
(() => {
  const legendClass = 'skio-plan-picker__legend';
  const pickerSelector = 'fieldset.skio-plan-picker';

  function legendText() {
    const strings = window.accessibilityStrings || {};
    return strings.purchaseOptions || 'Purchase options';
  }

  function addLegend(fieldset) {
    const existing = fieldset.querySelector(`legend.${legendClass}`);
    if (existing === fieldset.firstElementChild) return;
    if (existing) existing.remove();

    const legend = document.createElement('legend');
    legend.className = `${legendClass} visually-hidden`;
    legend.textContent = legendText();
    fieldset.prepend(legend);
  }

  function watchPicker(fieldset) {
    addLegend(fieldset);
    if (fieldset.skioLegendObserved) return;

    // Skio re-renders the picker after hydration, so restore the legend if it is dropped
    fieldset.skioLegendObserved = true;
    const observer = new MutationObserver(() => addLegend(fieldset));
    observer.observe(fieldset, { childList: true });
  }

  function addSkioLegends(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root.matches?.(pickerSelector)) watchPicker(root);
    root.querySelectorAll(pickerSelector).forEach(watchPicker);
  }

  // Quick add modals and section reloads inject their own copy of the picker
  const documentObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) addSkioLegends(node);
      }
    }
  });

  function initSkioLegends() {
    addSkioLegends(document);
    documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSkioLegends);
  } else {
    initSkioLegends();
  }
})();

// Initialize Rebuy focus management
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupRebuyFocusManagement());
} else {
  setupRebuyFocusManagement();
}
